import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { getAdminPhotos, getAdminCategories } from '../config/api';

export default function PhotoGalleryView() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const observerTarget = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [categoryName, setCategoryName] = useState('');
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [totalCount, setTotalCount] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(isMobile ? 12 : 20);

  const LIMIT = currentLimit;

  // Refs to track state without causing stale closures
  const isMountedRef = useRef(true);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update refs when state changes
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadPhotos = useCallback(async (currentOffset) => {
    try {
      const categoryParam = categoryId === 'all' ? null : categoryId;
      console.log('[PhotoGalleryView] Loading photos:', { categoryParam, currentOffset, LIMIT });
      const response = await getAdminPhotos(categoryParam, currentOffset, LIMIT);
      console.log('[PhotoGalleryView] API response:', response);
      const newPhotos = response.data?.data || [];
      const meta = response.data?.meta || {};
      console.log('[PhotoGalleryView] Parsed data:', { newPhotos, meta });

      if (!isMountedRef.current) return; // Don't update if unmounted

      setPhotos(prev => currentOffset === 0 ? newPhotos : [...prev, ...newPhotos]);
      setOffset(currentOffset + LIMIT);
      setHasMore(meta.hasMore || false);
      setTotalCount(meta.total || 0);
      console.log('[PhotoGalleryView] State updated:', { photosCount: newPhotos.length, totalCount: meta.total });

    } catch (err) {
      console.error('Failed to load more photos:', err);
      throw err;
    }
  }, [categoryId, LIMIT]);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPhotos([]);
    setOffset(0);
    setHasMore(true);

    try {
      // Get category name if not "all"
      if (categoryId !== 'all') {
        // Try to get name from navigation state first
        const categoryNameFromState = location.state?.categoryName;

        if (categoryNameFromState) {
          setCategoryName(categoryNameFromState);
          // Load photos immediately
          await loadPhotos(0);
        } else {
          // Fallback: fetch categories and photos in parallel (for direct URL navigation or refresh)
          const [categoriesRes] = await Promise.all([
            getAdminCategories(),
            loadPhotos(0)
          ]);

          const categories = categoriesRes.data?.data || [];
          const category = categories.find(c => String(c.id) === categoryId || c.id === parseInt(categoryId, 10));

          if (!category) {
            setError('Category not found');
            setTimeout(() => navigate('/admin/photo-gallery'), 2000);
            return;
          }

          setCategoryName(category.name);
        }
      } else {
        setCategoryName('All Categories');
        // Load initial photos
        await loadPhotos(0);
      }

    } catch (err) {
      console.error('Failed to load photos:', err);
      setError('Unable to load photos. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [categoryId, location.state, loadPhotos, navigate]);

  const loadMorePhotos = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      await loadPhotos(offset);
    } catch (err) {
      console.error('Failed to load more photos:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, loadPhotos, offset]);

  // Reset pagination when LIMIT changes due to screen resize
  useEffect(() => {
    const newLimit = isMobile ? 12 : 20;
    if (newLimit !== currentLimit) {
      setCurrentLimit(newLimit);
      loadInitial();
    }
  }, [isMobile, currentLimit, loadInitial]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (!observerTarget.current) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingMoreRef.current && !loading) {
          loadMorePhotos();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => observer.disconnect();
  }, [loading, loadMorePhotos]); // Only recreate when loading or loadMorePhotos changes

  const breadcrumbStyle = {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-muted)',
    marginBottom: 16,
  };

  const linkStyle = {
    color: 'var(--color-accent)',
    textDecoration: 'none',
    cursor: 'pointer',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 16,
    marginBottom: 32,
  };

  const photoStyle = {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: 4,
    backgroundColor: '#f0f0f0',
  };

  const mobileStyle = `
    @media (max-width: 768px) {
      .photo-grid {
        display: flex !important;
        flex-direction: column !important;
        gap: 8px !important;
      }

      .photo-grid img {
        width: 90% !important;
        margin: 0 auto !important;
        border-radius: 8px !important;
      }
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      .photo-grid {
        grid-template-columns: repeat(3, 1fr) !important;
      }
    }
  `;

  if (loading) {
    return (
      <AdminLayout title={categoryName || 'Photo Gallery'}>
        <style>{mobileStyle}</style>
        <div style={breadcrumbStyle}>
          <span style={linkStyle} onClick={() => navigate('/admin/photo-gallery')}>
            Photo Gallery
          </span>
          {' > '}
          <span>Loading...</span>
        </div>
        <div style={gridStyle} className="photo-grid">
          {[...Array(currentLimit)].map((_, i) => (
            <div
              key={i}
              style={{
                ...photoStyle,
                backgroundColor: '#e0e0e0',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          ${mobileStyle}
        `}</style>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Photo Gallery">
        <style>{mobileStyle}</style>
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          color: 'var(--color-error)',
          marginBottom: 16,
        }}>
          {error}
        </div>
        <button
          onClick={loadInitial}
          style={{
            padding: '10px 20px',
            background: 'var(--color-accent)',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            fontSize: 13,
            fontFamily: 'var(--font-body)',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={categoryName}>
      <style>{mobileStyle}</style>

      {/* Breadcrumb */}
      <div style={breadcrumbStyle}>
        <span
          style={linkStyle}
          onClick={() => navigate('/admin/photo-gallery')}
        >
          Photo Gallery
        </span>
        {' > '}
        <span>{categoryName}</span>
      </div>

      {/* Photo count */}
      <div style={{
        fontFamily: 'var(--font-body)',
        fontSize: 13,
        color: 'var(--color-muted)',
        marginBottom: 16,
      }}>
        {photos.length} of {totalCount} {totalCount === 1 ? 'photo' : 'photos'}
      </div>

      {/* Photo grid/feed */}
      {photos.length === 0 ? (
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          color: 'var(--color-muted)',
          textAlign: 'center',
          padding: 40,
        }}>
          No photos in this category yet.{' '}
          <span
            style={linkStyle}
            onClick={() => navigate('/admin/photos')}
          >
            Upload photos
          </span>
        </div>
      ) : (
        <div style={gridStyle} className="photo-grid">
          {photos.map((photo, index) => (
            <img
              key={photo.id}
              src={photo.thumbnail_url}
              alt={photo.title || `Photo ${index + 1}`}
              style={photoStyle}
              loading="lazy"
              onError={(e) => {
                e.target.style.backgroundColor = '#e0e0e0';
                e.target.alt = 'Failed to load';
              }}
            />
          ))}
        </div>
      )}

      {/* Loading more indicator */}
      {loadingMore && (
        <div style={{
          textAlign: 'center',
          padding: 20,
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          color: 'var(--color-muted)',
        }}>
          Loading more...
        </div>
      )}

      {/* Intersection observer target */}
      <div ref={observerTarget} style={{ height: 20 }} />
    </AdminLayout>
  );
}
